import getLang from 'I18n/utils/getLang';
import enviro from 'enviro';
import { QA } from 'self-service-api/constants/Environments';
import { getIdentificationToken } from 'ui-addon-upgrades/_core/common/api/getIdentificationToken';
import { getUserInfo } from 'ui-addon-upgrades/_core/common/api/getUserInfo';
import { getUpgradeDataTrackingProperties } from 'ui-addon-upgrades/ums/utils';
import { track } from '../common/eventTracking/tracker';
import { logRavenException } from './errors';

// eslint-disable-next-line hubspot-dev/no-declarations

const openSalesChat = (hostWindow, upgradeData) => {
  var _hostWindow$hubSpot;
  if (!hostWindow.HubSpotConversations) {
    logRavenException({
      customMessage: 'Attempted to open chat without the widget being loaded',
      error: Error('Attempted to open chat without the widget being loaded'),
      fingerprint: ['sales-chat-widget-not-loaded'],
      location: hostWindow.location.href
    });
    return;
  }
  if ((_hostWindow$hubSpot = hostWindow.hubSpot) !== null && _hostWindow$hubSpot !== void 0 && _hostWindow$hubSpot.zorse && !hostWindow.hubSpot.zorse.supressed) {
    hostWindow.hubSpot.zorse.minimizeZorseWidgetButton();
  }
  hostWindow.HubSpotConversations.on('conversationStarted', __payload => {
    track('communicationMethodsInteraction', Object.assign({
      action: 'started conversation'
    }, getUpgradeDataTrackingProperties(upgradeData)));
  });
  hostWindow.HubSpotConversations.on('conversationClosed', __payload => {
    var _hostWindow$hubSpot2;
    track('communicationMethodsInteraction', Object.assign({
      action: 'ended conversation'
    }, getUpgradeDataTrackingProperties(upgradeData)));
    hostWindow.HubSpotConversations.widget.close();
    if (hostWindow !== null && hostWindow !== void 0 && (_hostWindow$hubSpot2 = hostWindow.hubSpot) !== null && _hostWindow$hubSpot2 !== void 0 && _hostWindow$hubSpot2.zorse && !hostWindow.hubSpot.zorse.supressed) {
      hostWindow.hubSpot.zorse.restoreZorseWidgetButton();
    }
  });
  const status = hostWindow.HubSpotConversations.widget.status();
  const loadWidget = widgetOpen => {
    return new Promise(resolve => {
      hostWindow.HubSpotConversations.widget.load({
        widgetOpen
      });
      setTimeout(resolve, 500);
    });
  };

  // If widget is already loaded on page (eg paywalls), we need to remove and reload it to get new query params
  if (status.loaded) {
    hostWindow.HubSpotConversations.widget.remove();
    loadWidget(true).then(() => {
      hostWindow.HubSpotConversations.widget.open();
    }).catch(error => {
      logRavenException({
        customMessage: 'Failed to open chat after reloading',
        error,
        fingerprint: ['sales-chat-reload-failed']
      });
    });
  } else {
    loadWidget(true).then(() => {
      hostWindow.HubSpotConversations.widget.open();
    }).catch(error => {
      logRavenException({
        customMessage: 'Failed to open chat',
        error,
        fingerprint: ['sales-chat-load-failed']
      });
    });
  }
};
const mountConversationsScriptTag = hostWindow => {
  const qa = enviro.isProd() ? '' : QA;
  const chatPortal = enviro.isProd() ? '53' : '100048175';
  const script = hostWindow.document.createElement('script');
  const env = enviro.getShort();
  const hublet = enviro.getHublet();
  script.type = 'text/javascript';
  script.id = 'hubspot-messages-loader';
  script.async = false;
  script.defer = false;
  script.src = `//js.usemessages${qa}.com/conversations-embed.js`;
  script.setAttribute('data-hsjs-portal', chatPortal);
  script.setAttribute('data-hsjs-env', env);
  script.setAttribute('data-hs-js-hublet', hublet);
  hostWindow.document.body.appendChild(script);
};
const addChatQueryParams = (hostWindow, isRetail, isAssignable) => {
  const getQueryParam = () => {
    if (isRetail) {
      return `show-retail-tts-chat-${getLang()}`;
    } else if (isAssignable) {
      return `show-assignable-tts-chat-${getLang()}`;
    } else {
      return `show-unassignable-tts-chat-${getLang()}`;
    }
  };
  const queryParam = `${hostWindow.location.search.length ? '&' : '?'}${getQueryParam()}=true`;
  const queryParamIsPresent = hostWindow.location.search.includes(queryParam.slice(1));
  if (!queryParamIsPresent) {
    hostWindow.history.pushState(null, '', `${hostWindow.location.href}${queryParam}`);
  }
};
const initializeChatSettings = (hostWindow, isRetail, isAssignable) => {
  addChatQueryParams(hostWindow, isRetail, isAssignable);
  return Promise.all([getUserInfo(), getIdentificationToken()]).then(([userInfo, tokenObject]) => {
    const userEmail = userInfo.user.email;
    const token = tokenObject.token;
    if (!hostWindow.hsConversationsSettings) {
      hostWindow.hsConversationsSettings = {};
    }
    hostWindow.hsConversationsSettings.loadImmediately = false;
    hostWindow.hsConversationsSettings.identificationEmail = userEmail;
    hostWindow.hsConversationsSettings.identificationToken = token;
  }).catch(error => {
    logRavenException({
      customMessage: 'Failed to initialize chat settings',
      error,
      fingerprint: ['initializeChatSettings']
    });
    if (!hostWindow.hsConversationsSettings) {
      hostWindow.hsConversationsSettings = {};
    }
    hostWindow.hsConversationsSettings.loadImmediately = true;
  });
};

/**
 * Adds the required query parameter, loads the Conversations script tag,
 * and opens the chat when it's ready.
 *
 *
 * Note that `show-tts-chat=true` is a specific query parameter for
 * the [chatflow in 53](https://app.hubspot.com/chatflows/53/edit/live-chat/548260/target).
 */

export const loadAndOpenSalesChatInWindow = hostWindow => ({
  upgradeData,
  isRetail = false,
  isAssignable = false
}) => {
  if (hostWindow) {
    let windowToUse = hostWindow;
    // When in an iframe, the hostWindow is the parent
    if (hostWindow.self !== hostWindow.top) {
      windowToUse = hostWindow.parent;
    }
    initializeChatSettings(windowToUse, isRetail, isAssignable).then(() => {
      if (windowToUse.HubSpotConversations) {
        openSalesChat(windowToUse, upgradeData);
      } else {
        if (!Array.isArray(windowToUse.hsConversationsOnReady)) {
          windowToUse.hsConversationsOnReady = [];
        }
        const openChatCallback = () => {
          try {
            openSalesChat(windowToUse, upgradeData);
          } catch (error) {
            logRavenException({
              customMessage: 'Failed to open chat',
              error,
              fingerprint: ['sales-chat-callback-error']
            });
          }
        };
        windowToUse.hsConversationsOnReady.push(openChatCallback);
        mountConversationsScriptTag(windowToUse);
      }
    }).catch(error => {
      logRavenException({
        customMessage: 'Failed to initialize chat settings',
        error,
        fingerprint: ['loadAndOpenSalesChatInWindow']
      });
    });
  }
};

/**
 * Opens the HubSpot sales chat widget
 * @param params Configuration options
 * @param params.upgradeData {@link https://git.hubteam.com/HubSpot/ui-addon-upgrades/blob/master/ui-addon-upgrades/static-9.17103/js/_core/common/data/upgradeData/interfaces/upgradeDataPropsInterface.ts#L16-L33 UpgradeData} object containing upgrade context information (required)
 * @param params.isRetail Whether this is a retail chat flow (defaults to false)
 * @param params.isAssignable Whether this chat can be assigned to sales agents (defaults to false)
 * @returns A function that opens the sales chat widget with the provided parameters
 */
export const loadAndOpenSalesChat = params => loadAndOpenSalesChatInWindow(window)(params);